import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  IMAGE_ACCEPT,
  IMAGE_UPLOAD_HINT,
  MAX_UPLOAD_BYTES,
  describeImageUploadError,
  getImageUploadBlockReason,
  prepareImagesForUpload,
} from "../utils/imageUploadPolicy";
import ImageUploadHint from "./ImageUploadHint";

/**
 * The one image upload field used across the product.
 *
 * It owns the whole job: file input, size/type pre-check, HEIC conversion, the multipart upload,
 * the multi/single value, previews, remove, the primary badge and the guidance line. Pages pass a
 * value and get the new value back, so no page has to re-implement upload logic.
 *
 *   <ImageUploadField
 *     label="Product Images"
 *     multiple
 *     max={4}
 *     value={formData.images}
 *     onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
 *     endpoint={`${API_BASE_URL}/api/v2/products/images`}
 *   />
 *
 * `endpoint` may be absolute, as above and as every page passes it, or relative (`/api/...`) -
 * a relative path is resolved against `REACT_APP_API_BASE_URL` here, because a relative upload
 * would otherwise be sent to the frontend host and come back as a 405 from the static site.
 *
 * Server contract: POST multipart to `endpoint` with `fieldName` repeated per file, plus
 * `extraFields`; responds `{ urls: string[] }` (the product endpoint also returns `images[]`).
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/** Absolute endpoints are used as-is; relative ones get the API host in front. */
function resolveEndpoint(endpoint) {
  const value = String(endpoint || "");
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_BASE_URL}${value}`;
}

const DEFAULT_ENDPOINT = "/api/v2/products/images";

/**
 * Pull image URLs out of whatever shape an endpoint returns. The shared product endpoint returns
 * `{ urls, images[] }`; the dedicated single-image endpoints return `{ profilePicture }`,
 * `{ logo: { url } }`, `{ path }` and friends, so look for the usual names before giving up.
 */
function extractUrls(data) {
  if (!data) return [];
  if (Array.isArray(data.urls)) return data.urls.filter(Boolean);
  if (Array.isArray(data.images)) {
    return data.images
      .map((entry) => (typeof entry === "string" ? entry : entry?.url))
      .filter(Boolean);
  }
  if (typeof data.url === "string" && data.url) return [data.url];

  const singleFields = [
    "imageUrl",
    "logoUrl",
    "profilePicture",
    "brandImage",
    "serviceImage",
    "image",
    "path",
  ];

  for (const field of singleFields) {
    const candidate = data[field];
    if (typeof candidate === "string" && candidate) return [candidate];
    if (candidate && typeof candidate === "object" && typeof candidate.url === "string") {
      return [candidate.url];
    }
  }

  for (const nested of ["logo", "business", "user", "staff", "shop"]) {
    const candidate = data[nested];
    if (candidate && typeof candidate === "object") {
      const nestedUrls = extractUrls(candidate);
      if (nestedUrls.length) return nestedUrls;
    }
  }

  return [];
}

const VARIANTS = {
  dropzone: {
    triggerPadding: "12px",
    triggerBorder: "2px dashed #d1d5db",
    triggerRadius: "6px",
    triggerBackground: "#f9fafb",
    triggerColor: "#6b7280",
    triggerFontSize: "14px",
    triggerIcon: "📸",
  },
  inline: {
    triggerPadding: "10px 16px",
    triggerBorder: "1px solid #d1d5db",
    triggerRadius: "6px",
    triggerBackground: "#ffffff",
    triggerColor: "#374151",
    triggerFontSize: "13px",
    triggerIcon: "🖼",
  },
  avatar: {
    triggerPadding: "10px 18px",
    triggerBorder: "1px solid #2d6da3",
    triggerRadius: "8px",
    triggerBackground: "#2d6da3",
    triggerColor: "#ffffff",
    triggerFontSize: "14px",
    triggerIcon: "",
  },
};

export default function ImageUploadField({
  value,
  onChange,
  multiple = false,
  max = 4,
  endpoint = DEFAULT_ENDPOINT,
  fieldName = "images",
  extraFields,
  label,
  buttonLabel,
  hint = IMAGE_UPLOAD_HINT,
  showHint = true,
  variant = "dropzone",
  previewHeight = 110,
  columns,
  showPreviews = true,
  showPrimaryBadge = true,
  disabled = false,
  onError,
  onRemove,
  mapResponse,
  uploadOnSelect = true,
  onSelectFiles,
  localFiles = [],
  style,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localPreviews, setLocalPreviews] = useState([]);

  const urls = useMemo(() => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
  }, [value]);

  // Deferred mode previews the chosen files locally until the page uploads them.
  const previews = useMemo(() => {
    if (uploadOnSelect) {
      return urls.map((url) => ({ url, uploaded: true }));
    }
    return localFiles.map((file, index) => ({
      url: localPreviews[index] || "",
      uploaded: false,
      file,
    }));
  }, [uploadOnSelect, urls, localFiles, localPreviews]);

  useEffect(
    () => () => {
      localPreviews.forEach((url) => URL.revokeObjectURL(url));
    },
    [localPreviews],
  );

  const variantStyles = VARIANTS[variant] || VARIANTS.dropzone;

  const reportError = (error) => {
    const message = describeImageUploadError(error);
    if (onError) onError(message);
    else window.alert(message);
  };

  const commit = (nextUrls) => {
    if (!onChange) return;
    onChange(multiple ? nextUrls : nextUrls[0] || "");
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const remaining = multiple ? Math.max(0, max - urls.length) : 1;
    const accepted = files.slice(0, remaining);

    if (!accepted.length) {
      reportError(new Error(`You can add up to ${max} images.`));
      return;
    }

    if (files.length > accepted.length) {
      reportError(new Error(`You can add up to ${max} images.`));
    }

    const blocked = accepted.find((file) => getImageUploadBlockReason(file));
    if (blocked) {
      reportError(new Error(getImageUploadBlockReason(blocked)));
      return;
    }

    setUploading(true);

    try {
      const prepared = await prepareImagesForUpload(accepted);

      if (!uploadOnSelect) {
        const nextFiles = multiple
          ? [...localFiles, ...prepared].slice(0, max)
          : prepared.slice(0, 1);
        const nextPreviews = nextFiles.map((file) => URL.createObjectURL(file));
        localPreviews.forEach((url) => URL.revokeObjectURL(url));
        setLocalPreviews(nextPreviews);
        onSelectFiles?.(multiple ? nextFiles : nextFiles[0]);
        return;
      }

      const formData = new FormData();
      prepared.forEach((file) => formData.append(fieldName, file));
      Object.entries(extraFields || {}).forEach(([key, entry]) => {
        if (entry !== undefined && entry !== null && entry !== "") {
          formData.append(key, entry);
        }
      });

      const response = await axios.post(resolveEndpoint(endpoint), formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const uploaded = mapResponse
        ? mapResponse(response.data).filter(Boolean)
        : extractUrls(response.data);

      if (!uploaded.length) {
        throw new Error("The upload did not return an image URL.");
      }

      const next = multiple
        ? [...urls, ...uploaded].slice(0, max)
        : [uploaded[0]];

      commit(multiple ? next : next);
    } catch (error) {
      reportError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index) => {
    if (!uploadOnSelect) {
      const nextFiles = localFiles.filter((_, i) => i !== index);
      const nextPreviews = localPreviews.filter((_, i) => i !== index);
      URL.revokeObjectURL(localPreviews[index]);
      setLocalPreviews(nextPreviews);
      onSelectFiles?.(multiple ? nextFiles : nextFiles[0]);
      onRemove?.(index);
      return;
    }
    commit(urls.filter((_, i) => i !== index));
    // Reported separately from onChange so a page can persist the removal server-side. onChange
    // only carries the new value, and for a single-value field that is indistinguishable from
    // "never set", which is why a logo could be cleared in the UI and reappear on reload.
    onRemove?.(index);
  };

  const trigger = (
    <button
      type="button"
      // Deliberately NOT disabled when a single value is already set: the label
      // below promises "Replace image", so disabling here made that branch dead
      // code and left every single-image field un-clickable once it had a value.
      disabled={disabled || uploading}
      onClick={() => inputRef.current?.click()}
      style={{
        width: variant === "dropzone" ? "100%" : undefined,
        padding: variantStyles.triggerPadding,
        border: variantStyles.triggerBorder,
        borderRadius: variantStyles.triggerRadius,
        background: variantStyles.triggerBackground,
        color: variantStyles.triggerColor,
        fontSize: variantStyles.triggerFontSize,
        fontWeight: 500,
        cursor: disabled || uploading ? "wait" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      {variantStyles.triggerIcon ? (
        <span style={{ fontSize: "20px" }}>{variantStyles.triggerIcon}</span>
      ) : null}
      {uploading
        ? "Uploading…"
        : buttonLabel ||
          (multiple
            ? `Upload up to ${max} images`
            : previews.length
              ? "Replace image"
              : "Upload image")}
    </button>
  );

  return (
    <div style={style}>
      {label ? (
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {label}
        </label>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = "";
        }}
        style={{ display: "none" }}
      />

      {trigger}

      {showHint ? (
        hint && hint !== IMAGE_UPLOAD_HINT ? (
          <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
            {hint}
          </div>
        ) : (
          <ImageUploadHint />
        )
      ) : null}

      {showPreviews && previews.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${columns ? `calc(${100 / columns}% - 8px)` : "100px"}, 1fr))`,
            gap: "8px",
            marginTop: "12px",
          }}
        >
          {previews.map((preview, index) => (
            <div
              key={`${index}-${preview.url}`}
              style={{
                position: "relative",
                paddingTop: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "2px solid #e5e7eb",
              }}
            >
              {preview.url ? (
                <img
                  src={preview.url}
                  alt={`Upload ${index + 1}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : null}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                aria-label={`Remove image ${index + 1}`}
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  fontSize: "16px",
                  lineHeight: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  fontWeight: "bold",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                ×
              </button>
              {showPrimaryBadge && index === 0 ? (
                <div
                  style={{
                    position: "absolute",
                    bottom: "4px",
                    left: "4px",
                    background: "#10b981",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "9px",
                    fontWeight: 600,
                  }}
                >
                  PRIMARY
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { MAX_UPLOAD_BYTES };
